import React, { Suspense } from 'react';
import { useIdentityContext } from '@design-stack-vista/identity-provider';

import {
  ModalDialog,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
} from '@vp/swan';
import { useProductContext } from 'src/hooks/ProductContextHooks';
import { useTrackingEvent } from 'src/hooks/useTrackingEvent';
import { usePromptContext } from 'src/providers/PromptContextProvider';
import { UPLOAD_UNIFIED_EVENT, UploadSteps } from 'src/utils/Constants';
import { currentCulture } from 'src/utils/cultureUtil';
import { text } from 'src/utils/localizationUtil';
import { AnalyticsEventData } from '../Analytics';
import { PrintGuidelinesButton } from '../UploadAndPrint/PrintGuidelines';

const SpecsAndTemplatesLazy = React.lazy(() =>
  import(/* webpackChunkName: "SpecsAndTemplates" */ '@design-stack-vista/specs-and-templates').then(mod => ({
    default: mod.SpecsAndTemplates,
  }))
);
interface Props {
  isMobile: boolean;
}

export const SpecAndTemplateModalDialog = ({ isMobile }: Props) => {
  const { showSpecsAndTemplatePrompt, setShowSpecsAndTemplatePrompt } = usePromptContext();
  const { auth: vpAuth, isIdentityInitialized } = useIdentityContext();
  const { completedProductInformation } = useProductContext();
  const { sendSegmentTrackingEvent } = useTrackingEvent();

  const handleModalClose = () => {
    setShowSpecsAndTemplatePrompt(false);
  };

  const onFileDownload = (fileExtension: string) => {
    sendSegmentTrackingEvent({
      action: UPLOAD_UNIFIED_EVENT,
      uploadStep: UploadSteps.TemplateDownloaded,
      category: 'Design - Upload and Print',
      label: `Downloaded ${fileExtension} template file`,
      fileType: fileExtension,
    } as AnalyticsEventData);
  };

  if (!isIdentityInitialized || !completedProductInformation) {
    return null;
  }

  const localizationKeys = {
    template: text('specs-and-template.template-heading'),
    templateDesc: text('specs-and-template.template-desc'),
    designSpec: text('specs-and-template.designSpec'),
    designSpecDescLine: text('specs-and-template.designSpecDescLine'),
    bleedLabel: text('specs-and-template.bleedLabelSpecAndTemplates'),
    trimLabel: text('specs-and-template.trimLabelSpecAndTemplates'),
    safeLabel: text('specs-and-template.safeLabel'),
    height: text('specs-and-template.height'),
    width: text('specs-and-template.width'),
    lengthInInch: text('specs-and-template.lengthInInch'),
    widthInInch: text('specs-and-template.widthInInch'),
    lengthInMM: text('specs-and-template.lengthInMM'),
    widthInMM: text('specs-and-template.widthInMM'),
    safeArea: text('specs-and-template.safeArea'),
  };

  return (
    <ModalDialog
      variant={isMobile ? 'panel-bottom' : 'panel-right'}
      isOpen={showSpecsAndTemplatePrompt}
      onRequestDismiss={handleModalClose}
      onlyRenderWhenOpen
    >
      <ModalDialogContent aria-labelledby="modal dialog panel">
        <ModalDialogNav>
          <ModalDialogCloseButton accessibleText={text('specs-and-template.closeAccessibilityLabel')} />
        </ModalDialogNav>
        <ModalDialogHeader>
          <ModalDialogTitle px="6">{completedProductInformation.productName}</ModalDialogTitle>
        </ModalDialogHeader>
        <ModalDialogBody>
          <Suspense fallback={null}>
            <SpecsAndTemplatesLazy
              authTokenOrHeader={{ type: 'header', header: vpAuth.getAuthorizationHeader() }}
              hidePreview={true}
              productKey={completedProductInformation.productKey}
              productVersion={completedProductInformation.productVersion}
              selectedOptions={completedProductInformation.selectedOptions}
              culture={currentCulture.toLocaleLowerCase()}
              localizedValues={localizationKeys}
              onFileDownload={onFileDownload}
              guidelinesButtonLabel={text('specs-and-template.artwork-guideline-label')}
            />
          </Suspense>
          <PrintGuidelinesButton isMobile={isMobile} />
        </ModalDialogBody>
      </ModalDialogContent>
    </ModalDialog>
  );
};
